/**
 * The ⓘ "What the chips mean" that ships with every screen that shows proof
 * chips, and the per-person on/off switch (on by default; anyone can turn it off).
 *
 * The pictures are real renders of the chip and sheet (proof-chips-proof.test.tsx
 * writes them; threads/proof-chips/proof/shoot-howto.mjs marks them up with
 * the orange ring and tag) and live in client/public/how-to/.
 */
import { toast } from "sonner";
import { HowItWorks, type HowStep } from "@/components/how-it-works";
import { cn } from "@/lib/utils";

export const PROOF_CHIPS_HELP_TITLE = "What the chips mean";

export const PROOF_CHIPS_HELP_STEPS: HowStep[] = [
    {
        title: "Look at the symbol.",
        text: "✓ means we have the paper. ~ means it's GWC's best guess, so check before you trust it. ? means nothing is on file yet.",
        picture: "proof-chips-symbols.png",
        alt: "Three chips under a due date: a green check, an amber squiggle and a dashed question mark",
    },
    {
        title: "Tap it.",
        text: "You'll see where it came from, when, and who. The button at the bottom fixes it. Fixing it turns the chip into a ✓.",
        picture: "proof-chips-sheet.png",
        alt: "The sheet that opens from a Best guess chip, with the Upload the report button ringed",
    },
    {
        title: "A small gray ✓ on a list means proven.",
        text: "Lists only show the gray ✓ so the doubtful ones stand out. Point at it and it turns green. Tap it to see the proof.",
        picture: "proof-chips-quiet.png",
        alt: "A list row with a small gray check next to the date",
    },
];

export function ProofChipsHelp({ className }: { className?: string }) {
    return (
        <HowItWorks
            title={PROOF_CHIPS_HELP_TITLE}
            steps={PROOF_CHIPS_HELP_STEPS}
            className={className}
            testId="proof-chips-help"
        />
    );
}

/**
 * "How sure: Off | On" — the per-person opt-out (on by default), drawn where
 * the chips appear.
 * Only the person's own screen changes.
 */
export function ProofChipsToggle({
    on,
    onChange,
    className,
}: {
    on: boolean;
    onChange: (on: boolean) => void;
    className?: string;
}) {
    const pick = (want: boolean) => {
        if (want === on) return;
        onChange(want);
        toast.success(
            want ? "How-sure chips on. Only your screen changed." : "How-sure chips off. Only your screen changed.",
        );
    };
    const base =
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
    return (
        <div
            role="radiogroup"
            aria-label="Show how sure each date is"
            data-testid="proof-chips-toggle"
            title="Show how sure each date is. Only changes your own screen."
            className={cn("inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground", className)}
        >
            <span className="font-semibold">How sure:</span>
            <span className="inline-flex items-center gap-0.5 rounded-full border bg-card p-0.5">
                {[false, true].map((v) => (
                    <button
                        key={String(v)}
                        type="button"
                        role="radio"
                        aria-checked={on === v}
                        data-testid={v ? "proof-chips-on" : "proof-chips-off"}
                        onClick={() => pick(v)}
                        className={cn(
                            base,
                            on === v
                                ? "bg-foreground text-background"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                        )}
                    >
                        {v ? "On" : "Off"}
                    </button>
                ))}
            </span>
        </div>
    );
}
